/* Question wording, answer values and branches ported from Backbone's /quiz. */
(() => {
  // Loaded after quiz-form.js (snippets/quiz-config.liquid), whose pieces every quiz page shares.
  const shared = window.MosesQuizForm;
  if (!shared) return;
  const { SUBMIT_CLASS, typographic, progress, contactLine } = shared;
  const IN_TREATMENT = "I'm currently in treatment";
  // Public site/list IDs only. RnkAh7 uses Klaviyo double opt-in: a 202 means
  // requested, never confirmed. Treatment dates remain owned by Backbone.
  async function requestTreatmentReminder(email) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch("https://a.klaviyo.com/client/subscriptions?company_id=TZwzVT", {
        method: "POST",
        credentials: "omit",
        referrerPolicy: "no-referrer",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/vnd.api+json",
          revision: "2026-10-15",
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "moses treatment quiz | reminder opt-in v1",
              profile: { data: { type: "profile", attributes: {
                email,
                subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
              } } },
            },
            relationships: { list: { data: { type: "list", id: "RnkAh7" } } },
          },
        }),
      });
      if (response.status !== 202) throw new Error("Reminder request failed");
    } finally {
      clearTimeout(timer);
    }
  }
  const SUBMISSION = { quiz_version: "v1", funnel_variant: window.MosesQuiz?.read().funnelVariant || "cleaner-retainer" };
  const PURCHASE_SUBMISSION = { ...SUBMISSION, quiz_version: "purchase-v1" };
  const AD_QUIZ_VERSION = "ad-v2";
  const AD_SUBMISSION = { ...SUBMISSION, quiz_version: AD_QUIZ_VERSION };
  const questions = [
    {
      key: "q1",
      question: "Have you ever worn braces, Invisalign, or clear aligners?",
      options: ["Yes", "No", IN_TREATMENT],
    },
    {
      key: "q2",
      question: "When did you finish treatment?",
      options: [
        "Within the last year",
        "1–3 years ago",
        "3–5 years ago",
        "5+ years ago",
      ],
    },
    {
      key: "qTreatmentType",
      question: "What type of orthodontic treatment did you have?",
      options: ["Braces", "Aligners", "Invisalign", "Lingual Braces", "Other"],
    },
    {
      key: "qTreatmentProvider",
      question: "Who did your treatment?",
      options: ["Dentist", "Orthodontist", "Online Service", "Other"],
      // What the option shows; the option itself is what Backbone receives, unchanged.
      display: {
        "Online Service": "Online service (e.g. Smileset, Candid, Smile Direct Club)",
      },
    },
    {
      key: "q3",
      question: "Do you currently wear a retainer?",
      options: [
        "Every night",
        "A few nights a week",
        "A couple nights a month",
        "Stopped wearing it",
        "Don't have one anymore",
      ],
    },
    {
      key: "q4",
      question: "How long have you had your current retainer?",
      options: [
        "Less than 6 months",
        "6 months – 1 year",
        "1–2 years",
        "2–5 years",
        "5+ years",
      ],
    },
    {
      key: "q5",
      question: "What are your biggest issues with your retainer?",
      freetext: true,
    },
    {
      key: "q6",
      question:
        "How much have you reduced microplastics in other parts of your life?",
      options: [
        "A lot: water bottles, food storage, personal care, the works",
        "Some: I've made a few changes",
        "A little: I'm aware but haven't done much",
        "None",
      ],
    },
    {
      key: "q8",
      question:
        "What concerns you most about microplastics?",
      hint: "Select all that apply",
      multi: true,
      options: [
        "Long-term accumulation in my body",
        "Hormone or fertility impact",
        "My kids' exposure",
        "Cognitive or brain impact",
        "Gut and digestive health",
        "Not sure. I'm here to learn",
      ],
    },
  ];
  const treatmentQuestion = {
    key: "q_treatment_end",
    question: "When do you expect to finish treatment?",
    hint: "An estimate is fine. Treatment dates can change.",
    monthYear: true,
  };
  const treatmentMonths = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const treatmentBucket = (year, month) => {
    const today = new Date();
    const monthsAway = (Number(year) - today.getFullYear()) * 12 + Number(month) - today.getMonth() - 1;
    if (monthsAway <= 3) return "Within the next 3 months";
    if (monthsAway <= 6) return "3–6 months";
    if (monthsAway <= 12) return "6–12 months";
    return "More than a year away";
  };
  const steps = [
    "Retainer history",
    "Wear patterns",
    "Health priorities",
    "Checking eligibility",
  ];
  // A buyer arriving from the cart only needs to confirm treatment and current
  // retainer use. The longer educational quiz remains available for ad visitors.
  const purchaseQuestions = [
    {
      ...questions[0],
      question: "Have you finished braces or aligner treatment?",
      display: { No: "I’ve never had braces or aligners" },
    },
    {
      key: "q3",
      question: "Do you currently wear a retainer?",
      options: [
        "I wear one regularly",
        "I wear one sometimes",
        "I lost it",
        "I stopped wearing it",
        "It no longer fits",
      ],
    },
  ];
  const lastWornQuestion = {
    key: "q_last_worn",
    question: "When did you last wear your retainer?",
    options: [
      "Within the past month",
      "1–3 months ago",
      "3–6 months ago",
      "6–12 months ago",
      "More than a year ago",
    ],
  };
  const adQuestions = [
    {
      key: "qTreatmentType",
      question: "What kind of treatment have you had?",
      options: ["Braces", "Invisalign", "Other clear aligners", "Still in treatment", "No orthodontic treatment"],
    },
    {
      key: "q2",
      question: "When did you finish treatment?",
      options: ["Within the past year", "1–3 years ago", "3–5 years ago", "More than 5 years ago", "Still in treatment", "I haven’t had treatment"],
    },
    {
      key: "qSwitchReason",
      question: "What made you consider switching?",
      hint: "Choose the main reason.",
      options: [
        "I want less plastic in my daily routine",
        "I need a new retainer",
        "I want to know more about the material",
        "I’m just exploring",
      ],
    },
    questions[7],
    questions[8],
    {
      ...questions[6],
      question: "What do you dislike about your current or last retainer?",
      hint: "Optional. Skip if you haven’t worn one.",
      placeholder: "Tell us what bothers you about it.",
    },
  ];
  const el = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const supportEmail = (address, subject, body) =>
    `mailto:${address}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  class MosesQuiz extends HTMLElement {
    connectedCallback() {
      this.api = window.MosesQuiz;
      if (!this.api) return;
      const adEntry = new URLSearchParams(location.search).get("entry") === "ad";
      const saved = this.api.read();
      const directPdp = adEntry && saved.funnelVariant === "quiz-v2";
      const resumeWithoutEmail = directPdp && ["email-qualified", "analysis-email"].includes(saved.screen);
      const fresh = { answers: {}, step: 0, screen: "question", qualified: false, attributes: null, eventId: null, emailEventId: null, email: "" };
      if (adEntry)
        this.api.save({
          ...(saved.adFlow && saved.adQuizVersion === AD_QUIZ_VERSION && !saved.checkoutIntent ? {} : fresh),
          adFlow: true, adQuizVersion: AD_QUIZ_VERSION, emailGate: !directPdp, checkoutIntent: false,
          pending: null, resume: false, returnUrl: directPdp ? saved.returnUrl || this.api.config.productUrl : this.api.config.productUrl,
          ...(resumeWithoutEmail ? { screen: "question" } : {}),
        });
      else if (saved.adFlow && !saved.checkoutIntent)
        this.api.save({ ...fresh, adFlow: false, adQuizVersion: null, emailGate: false });
      this.state = this.api.read();
      this.purchaseFlow = this.state.checkoutIntent && !this.state.emailGate;
      this.adFlow = adEntry && !this.purchaseFlow;
      this.state.answers ||= {};
      this.syncQuestions();
      this.state.step = Math.max(
        0,
        Math.min(this.activeQuestions.length - 1, Number(this.state.step) || 0),
      );
      // Never repeat a write just because a loading screen was reloaded.
      if (this.state.screen === "analysis") this.state.screen = "question";
      if (this.state.screen === "analysis-email") this.state.screen = "email-qualified";
      this.controller?.abort();
      this.controller = new AbortController();
      this.render(false);
      if (resumeWithoutEmail) void this.complete();
      if (this.state.screen === "qualified" && this.state.checkoutIntent)
        void this.finish();
      const home = document.querySelector(".quiz-header a");
      if (home && !home.dataset.quizBound) {
        home.dataset.quizBound = "";
        home.addEventListener("click", () =>
          this.persist({ checkoutIntent: false }),
        );
      }
      this.api.track("quiz_started", { total_questions: this.activeQuestions.length });
    }
    disconnectedCallback() {
      clearTimeout(this.advanceTimer);
      clearInterval(this.analysisTimer);
      this.controller?.abort();
    }
    persist(patch = {}) {
      this.state = this.api.save({ ...this.state, ...patch });
    }
    get inTreatment() {
      return this.state.answers.q1 === IN_TREATMENT;
    }
    syncQuestions() {
      if (!this.purchaseFlow && !this.adFlow) {
        this.activeQuestions = questions;
        return;
      }
      const wear = this.state.answers.q3;
      const qualificationQuestions = [
        ...purchaseQuestions,
        ...(wear === "I lost it" || wear === "I stopped wearing it"
          ? [lastWornQuestion] : []),
      ];
      this.activeQuestions = this.adFlow
        ? [...adQuestions, ...qualificationQuestions]
        : qualificationQuestions;
    }
    question() {
      return this.inTreatment && this.activeQuestions[this.state.step]?.key === "q3"
        ? treatmentQuestion
        : this.activeQuestions[this.state.step];
    }
    valid() {
      const q = this.question(),
        answer = this.state.answers[q.key];
      if (q.monthYear) {
        if (answer === "Not sure") return true;
        const expected = this.state.answers.q_treatment_expected_month;
        const today = new Date();
        return /^\d{4}-(0[1-9]|1[0-2])$/.test(expected || "") &&
          `${expected}` >= `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
      }
      return (
        q.freetext ||
        (q.multi
          ? Array.isArray(answer) && answer.length > 0
          : q.options.includes(answer))
      );
    }
    button(text, action, className) {
      const button = el("button", text, className);
      button.type = "button";
      button.addEventListener("click", action);
      return button;
    }
    navButton(text, action, back) {
      const button = this.button("", action, back ? "quiz-back" : "quiz-next");
      const arrow = el("img");
      arrow.src = this.api.config.arrow;
      arrow.alt = "";
      arrow.width = 22;
      arrow.height = 8;
      const label = el("span", text);
      button.append(...(back ? [arrow, label] : [label, arrow]));
      return button;
    }
    backButton(action = () => this.back()) {
      return this.navButton("Back", action, true);
    }
    error(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
      if (message) this.errorEl.focus();
    }
    render(focus = true) {
      clearInterval(this.analysisTimer);
      clearTimeout(this.advanceTimer);
      this.advancing = false;
      this.replaceChildren();
      const screen = this.state.screen || "question";
      this.dataset.screen = screen;
      this.progress =
        screen === "question"
          ? progress(this.state.step + 1, this.inTreatment && !this.adFlow ? 2 : this.activeQuestions.length)
          : el("p", "", "quiz-progress");
      this.heading = el("h1", "", "quiz-heading");
      this.heading.id = "quiz-heading";
      this.heading.tabIndex = -1;
      this.fields = el("div", "", "quiz-fields");
      this.errorEl = el("p", "", "quiz-error");
      this.errorEl.hidden = true;
      this.errorEl.role = "alert";
      this.errorEl.tabIndex = -1;
      this.append(this.progress, this.heading, this.fields, this.errorEl);
      if (screen === "success") this.renderSuccess();
      else if (screen === "qualified") this.renderQualified();
      else if (screen === "check-in") this.renderCheckIn();
      else if (screen.startsWith("email-")) this.renderEmail(screen);
      else if (screen === "analysis" || screen === "analysis-email") this.renderAnalysis();
      else this.renderQuestion();
      if (focus) this.heading.focus();
    }
    renderQuestion() {
      const q = this.question();
      this.heading.textContent = q.question;
      this.heading.dataset.question = q.key;
      if ((this.purchaseFlow || this.adFlow) && q.key === "q1") {
        const intro = el("p", "We need to make sure this product is right for you. After you order, you'll go through a short onboarding to help us retrieve your existing dental scan or arrange a new one.", "quiz-description quiz-intro");
        const explainer = el("details", "", "quiz-scan-explainer");
        explainer.append(el("summary", "What is a dental scan?"));
        const content = el("div", "", "quiz-scan-explainer-content");
        const figure = el("figure", "", "quiz-scan-explainer-figure");
        const image = el("img");
        image.src = this.api.config.dentalScanImage;
        image.alt = "A handheld dental scanner beside a screen showing a 3D model of teeth";
        image.loading = "lazy";
        image.width = 1448;
        image.height = 1086;
        figure.append(image);
        content.append(figure, el("p", "If you finished braces or aligner treatment and wear a retainer now, your dentist or orthodontist likely scanned your teeth to make it. A dental scan is a 3D model made with an intraoral scanner (wand-like device). With your permission, we’ll request that scan from your dental office. If you don’t have one, we’ll book you a new scan at a practice near you and cover the cost."));
        explainer.append(content);
        this.heading.after(intro);
        intro.after(explainer);
      }
      if (q.hint) this.heading.after(el("p", q.hint, "quiz-description is-muted"));
      if (this.state.emailGate && !this.adFlow && this.state.step === 0) {
        this.heading.after(el(
          "p",
          "Find out whether moses is right for you.",
          "quiz-description quiz-intro",
        ));
      }
      if (q.monthYear) {
        this.renderTreatmentMonth();
      } else if (q.freetext) {
        const textarea = el("textarea", "", "quiz-field");
        textarea.name = q.key;
        textarea.setAttribute("aria-labelledby", "quiz-heading");
        textarea.maxLength = 2000;
        textarea.placeholder = q.placeholder ||
          "E.g. it smells, it’s cracking, gone cloudy, I can see flakes.";
        textarea.value = this.state.answers[q.key] || "";
        textarea.addEventListener("input", () => {
          this.state.answers[q.key] = textarea.value;
          this.persist();
        });
        this.fields.append(textarea);
      } else {
        const group = el("fieldset", "", "quiz-options");
        const legend = el("legend", q.question, "quiz-sr");
        group.append(legend);
        for (const option of q.options) {
          const label = el("label", "", "quiz-option");
          const input = el("input");
          input.type = q.multi ? "checkbox" : "radio";
          input.name = q.key;
          input.value = option;
          const answer = this.state.answers[q.key];
          input.checked = q.multi
            ? (answer || []).includes(option)
            : answer === option;
          input.addEventListener("change", () => {
            if (q.key === "q1" && option !== this.state.answers.q1) {
              if (this.adFlow) {
                delete this.state.answers.q3;
                delete this.state.answers.q_last_worn;
                delete this.state.answers.q_treatment_end;
                delete this.state.answers.q_treatment_expected_month;
              } else this.state.answers = {};
              this.state.qualified = false;
              this.state.attributes = null;
              this.state.eventId = null;
              this.state.emailEventId = null;
            }
            if ((this.purchaseFlow || this.adFlow) && (q.key === "q3" || q.key === "q_last_worn") && option !== this.state.answers[q.key]) {
              this.state.qualified = false;
              this.state.attributes = null;
              this.state.eventId = null;
              this.state.emailEventId = null;
              if (q.key === "q3") {
                delete this.state.answers.q_last_worn;
                delete this.state.answers.q_retainer_fit;
              }
            }
            if (q.multi)
              this.state.answers[q.key] = [
                ...group.querySelectorAll("input:checked"),
              ].map((i) => i.value);
            else this.state.answers[q.key] = option;
            if ((this.purchaseFlow || this.adFlow) && q.key === "q3") this.syncQuestions();
            this.persist();
            this.next.disabled = !this.valid();
            if (!q.multi) {
              // Arrow-key radio navigation must not unexpectedly move focus to another screen.
              clearTimeout(this.advanceTimer);
              if (!this.keyboardChoice)
                this.advanceTimer = setTimeout(() => this.advance(), 180);
            }
          });
          input.addEventListener("keydown", () => {
            this.keyboardChoice = true;
          });
          label.addEventListener("pointerdown", () => {
            this.keyboardChoice = false;
          });
          label.append(input, el("span", q.display?.[option] ?? typographic(option)));
          group.append(label);
        }
        this.fields.append(group);
        if ((this.purchaseFlow || this.adFlow) && q.key === "q3") {
          const help = el("a", "None of these? Get help", "quiz-help-link");
          const subject = "Help choosing a retainer option";
          const body = "Hi moses,\n\nI’m considering the Unplastic Retainer, but none of the answers about my current retainer fits my situation. Can you help me understand the right next step?\n\nThanks,";
          help.href = supportEmail(
            this.api.config.contactEmail,
            subject,
            body,
          );
          this.fields.append(help);
        }
      }
      const nav = el("nav", "", "quiz-nav");
      nav.setAttribute("aria-label", "Quiz navigation");
      nav.append(this.backButton());
      this.next = this.navButton("Continue", () => this.advance(), false);
      this.next.disabled = !this.valid();
      nav.append(this.next);
      this.fields.append(nav);
    }
    renderTreatmentMonth() {
      const group = el("fieldset", "", "quiz-treatment-date");
      group.append(el("legend", "Expected treatment end date", "quiz-sr"));
      const fields = el("div", "", "quiz-treatment-date-fields");
      const monthLabel = el("label", "", "quiz-treatment-date-label");
      monthLabel.append(el("span", "Month"));
      const month = el("select", "", "quiz-field");
      month.name = "q_treatment_month";
      month.append(new Option("Choose month", ""));
      treatmentMonths.forEach((name, index) => month.append(new Option(name, String(index + 1).padStart(2, "0"))));
      month.value = this.state.answers.q_treatment_month || "";
      monthLabel.append(month);
      const yearLabel = el("label", "", "quiz-treatment-date-label");
      yearLabel.append(el("span", "Year"));
      const year = el("select", "", "quiz-field");
      year.name = "q_treatment_year";
      year.append(new Option("Choose year", ""));
      const currentYear = new Date().getFullYear();
      for (let value = currentYear; value <= currentYear + 10; value++)
        year.append(new Option(String(value), String(value)));
      year.value = this.state.answers.q_treatment_year || "";
      yearLabel.append(year);
      const restrictPastMonths = () => {
        const currentMonth = new Date().getMonth() + 1;
        for (const option of month.options) {
          option.disabled = Boolean(option.value) && year.value === String(currentYear) && Number(option.value) < currentMonth;
        }
        if (month.selectedOptions[0]?.disabled) month.value = "";
      };
      restrictPastMonths();
      fields.append(monthLabel, yearLabel);
      const unsureLabel = el("label", "", "quiz-treatment-unsure quiz-option");
      const unsure = el("input");
      unsure.type = "checkbox";
      unsure.name = "q_treatment_unknown";
      unsure.checked = this.state.answers.q_treatment_end === "Not sure";
      unsureLabel.append(unsure, el("span", "I’m not sure yet"));
      const sync = () => {
        const answers = this.state.answers;
        if (unsure.checked) {
          answers.q_treatment_end = "Not sure";
          answers.q_treatment_expected_month = "";
        } else {
          answers.q_treatment_month = month.value;
          answers.q_treatment_year = year.value;
          answers.q_treatment_expected_month = month.value && year.value
            ? `${year.value}-${month.value}` : "";
          answers.q_treatment_end = answers.q_treatment_expected_month
            ? treatmentBucket(year.value, month.value) : "";
        }
        month.disabled = year.disabled = unsure.checked;
        this.persist();
        this.next.disabled = !this.valid();
      };
      month.addEventListener("change", () => { unsure.checked = false; sync(); });
      year.addEventListener("change", () => { unsure.checked = false; restrictPastMonths(); sync(); });
      unsure.addEventListener("change", sync);
      month.disabled = year.disabled = unsure.checked;
      group.append(fields, unsureLabel);
      this.fields.append(group);
    }
    advance() {
      if (this.advancing || !this.valid()) return;
      clearTimeout(this.advanceTimer);
      this.advancing = true;
      const q = this.question();
      this.api.track("quiz_question_answered", {
        question_key: q.key,
        question_num: this.state.step + 1,
        total_questions: this.activeQuestions.length,
      });
      const disqualified = { qualified: false, attributes: null };
      if (q.key === "q1" && this.state.answers.q1 === "No") {
        this.persist({ screen: "email-no", ...disqualified });
      } else if (q.key === "q_treatment_end") {
        this.persist({ screen: "email-treatment", ...disqualified });
      } else if ((this.purchaseFlow || this.adFlow) && (
        (q.key === "q3" && this.state.answers.q3 === "It no longer fits") ||
        (q.key === "q_last_worn" && this.state.answers.q_last_worn !== "Within the past month")
      )) {
        this.persist({ screen: "check-in", ...disqualified });
      } else if (this.state.step === this.activeQuestions.length - 1) {
        if (this.state.emailGate) {
          this.persist({ screen: "analysis-email" });
          this.api.track("quiz_completed", { total_questions: this.activeQuestions.length, quiz_version: this.adFlow ? AD_QUIZ_VERSION : "v1", funnel_variant: this.api.read().funnelVariant || "cleaner-retainer" });
          void this.transition("forward");
        } else if (this.api.qualified()) {
          void (this.purchaseFlow ? this.finishWithAnalysis() : this.finish());
        } else void this.complete();
        return;
      } else {
        this.persist({ step: this.state.step + 1, screen: "question" });
      }
      void this.transition("forward");
    }
    renderCheckIn() {
      this.renderEmail("email-followup");
    }
    renderNightGuard() {
      this.heading.textContent = "Looking for a night guard?";
      this.heading.after(el("p", "The Unplastic Retainer is for keeping teeth in place after braces or aligners. If you’ve never had orthodontic treatment, take a look at the Unplastic Night Guard we’re developing.", "quiz-description"));
      const nightGuard = el("a", "Explore the Night Guard", SUBMIT_CLASS);
      nightGuard.href = this.api.config.root + "pages/night-guard";
      this.fields.append(nightGuard, contactLine(this.api.config.contactEmail));
      const nav = el("nav", "", "quiz-nav");
      nav.append(this.backButton());
      this.fields.append(nav);
    }
    // Re-render behind the shared screen animation (quiz-form.js).
    transition(direction) {
      return shared.transition(this, direction, () => this.render());
    }
    back() {
      clearTimeout(this.advanceTimer);
      if ((this.state.screen || "").startsWith("email-") || this.state.screen === "check-in")
        this.persist({ screen: "question" });
      else if (this.state.step > 0)
        this.persist({ step: this.state.step - 1, screen: "question" });
      else {
        this.leave();
        return;
      }
      void this.transition("back");
    }
    leave() {
      this.persist({ checkoutIntent: false });
      location.assign(this.api.localUrl(this.state.returnUrl));
    }
    // Consent is confirmed by Klaviyo's email, never by the browser response.
    renderSuccess() {
      if (this.state.followupRequested) {
        this.heading.textContent = "Our team will be in touch.";
        this.heading.after(el("p", "We’ve saved your email and quiz answers. Our team will reach out to help with your next steps. Before ordering a replacement, ask your dentist or orthodontist to check whether a new retainer is right for you.", "quiz-description"));
        const home = el("a", "Return to moses", SUBMIT_CLASS);
        home.href = this.api.config.root;
        this.fields.append(home, contactLine(this.api.config.contactEmail));
        return;
      }
      const hasFinishMonth = Boolean(this.state.answers.q_treatment_expected_month);
      const reminderRequested = this.inTreatment && this.state.treatmentReminderRequest === "pending";
      const reminderFailed = this.inTreatment && this.state.treatmentReminderRequest === "failed";
      const description = this.inTreatment
        ? hasFinishMonth
          ? "We’ve saved your email and expected finish month. Once your dentist or orthodontist confirms your treatment is complete, come back to see if the Unplastic Retainer is right for you."
          : "We’ve saved your email. Once your dentist or orthodontist confirms your treatment is complete, come back to see if the Unplastic Retainer is right for you."
        : "We’ll email you when new moses products launch.";
      this.heading.textContent = this.inTreatment ? "Your details are saved." : "You’re on the list.";
      this.heading.after(el("p", description, "quiz-description"));
      if (reminderRequested) {
        this.heading.textContent = "Check your inbox.";
        this.heading.after(el("p", hasFinishMonth
          ? "Confirm your email to get a reminder about a month before your expected finish month. If you’re already subscribed, you’re all set."
          : "Confirm your email to get retainer updates from moses. We’ll need your expected finish month before we can time a reminder. If you’re already subscribed, you’re all set.", "quiz-description"));
      }
      if (reminderFailed) {
        this.fields.append(el("p", "Your details are saved, but we couldn’t request your confirmation email. Try again to sign up for emails.", "quiz-description"));
        const retry = this.button("Try email sign-up again", async () => {
          if (this.submitting) return;
          this.submitting = true;
          retry.disabled = true;
          retry.textContent = "Requesting…";
          await this.requestReminder();
          this.submitting = false;
          this.render();
        }, SUBMIT_CLASS);
        this.fields.append(retry);
      }
      const home = el("a", "Return to moses", SUBMIT_CLASS);
      home.href = this.api.config.root;
      this.fields.append(home, contactLine(this.api.config.contactEmail));
    }
    async requestReminder() {
      try {
        await requestTreatmentReminder(this.state.email);
        this.persist({ treatmentReminderRequest: "pending" });
      } catch {
        // Saving quiz answers succeeded. A retry must only repeat the consent request.
        this.persist({ treatmentReminderRequest: "failed" });
      }
    }
    renderEmail(screen) {
      if (screen === "email-no") {
        this.renderNightGuard();
        return;
      }
      const isQualified = screen === "email-qualified";
      const inTreatment = screen === "email-treatment";
      const followup = screen === "email-followup";
      const submitLabel = isQualified ? "See my retainer" : inTreatment ? "Save my details" : "Get help from the moses support team";
      this.heading.textContent = isQualified ? "You’re a great fit for moses." : inTreatment
        ? "Get ready for your first retainer."
        : "Let’s make sure a replacement is right for you.";
      if (isQualified) this.heading.after(el("p", "Enter your email to save your answers and see your retainer options.", "quiz-description"));
      if (inTreatment)
        this.heading.after(el("p", "After your dentist or orthodontist confirms treatment is complete, you’ll need a retainer to help keep your teeth in place. Save your details and choose an email reminder below. At your next appointment, ask about getting an intraoral scan. Our team can help with questions or materials for your dentist.", "quiz-description"));
      if (followup) {
        this.heading.after(el("p", "Your teeth may have moved, or your retainer may no longer fit. Ask your dentist or orthodontist to check whether a replacement retainer is right for you before ordering.", "quiz-description"));
      }
      const form = el("form");
      if (followup) form.append(el("p", "Leave your email and our team will reach out to help with next steps.", "quiz-question"));
      const label = el("label", "Your email address", "quiz-sr");
      label.htmlFor = "quiz-email";
      const email = el("input", "", "quiz-field");
      email.id = "quiz-email";
      email.type = "email";
      email.name = "email";
      email.required = true;
      email.autocomplete = "email";
      email.placeholder = "jane@example.com";
      email.value = this.state.email || "";
      email.addEventListener("input", () =>
        this.persist({ email: email.value }),
      );
      const submit = el("button", submitLabel, SUBMIT_CLASS);
      submit.type = "submit";
      form.append(label, email);
      let reminderConsent;
      if (inTreatment) {
        const consentLabel = el("label", "", "quiz-reminder-consent");
        reminderConsent = el("input");
        reminderConsent.type = "checkbox";
        reminderConsent.name = "treatment_reminder_consent";
        reminderConsent.checked = false;
        const consentText = el("span", this.state.answers.q_treatment_expected_month
          ? "Confirm your email to get a reminder about a month before your expected finish month. Unsubscribe anytime."
          : "Confirm your email to get retainer updates. We’ll need your expected finish month before we can time a reminder. Unsubscribe anytime.");
        consentLabel.append(reminderConsent, consentText);
        form.append(consentLabel);
      }
      form.append(submit);
      const back = this.backButton();
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (this.submitting || !form.reportValidity()) return;
        if (isQualified) {
          this.persist({ email: email.value.trim() });
          submit.disabled = email.disabled = back.disabled = true;
          submit.textContent = "Saving…";
          this.error("");
          try {
            await this.complete();
          } finally {
            submit.disabled = email.disabled = back.disabled = false;
            submit.textContent = submitLabel;
          }
          return;
        }
        this.submitting = true;
        const wantsReminder = inTreatment && reminderConsent?.checked === true;
        if (reminderConsent) reminderConsent.disabled = true;
        submit.disabled = true;
        email.disabled = true;
        back.disabled = true;
        submit.textContent = "Saving…";
        this.error("");
        this.persist({
          email: email.value.trim(),
          emailEventId: this.state.emailEventId || this.api.uuid(),
        });
        try {
          await this.api.post(
            "/v1/quiz-submissions",
              {
                  email: this.state.email,
                  answers: this.state.answers,
                  ...(this.purchaseFlow ? PURCHASE_SUBMISSION : this.adFlow ? AD_SUBMISSION : SUBMISSION),
                },
            this.state.emailEventId,
          );
          this.api.track("quiz_email_captured", {
            qualified: false,
            dq_reason: inTreatment ? "in-treatment" : "retainer-assessment",
            product_handle: "unplastic-retainer",
            eligibility_status: inTreatment ? "in-treatment" : "needs-dentist-review",
            capture_context: this.purchaseFlow ? "purchase" : this.adFlow ? "ad" : "quiz",
          });
          this.persist({ treatmentReminderRequest: null, followupRequested: followup });
          if (wantsReminder) await this.requestReminder();
          this.persist({ screen: "success" });
          this.render();
        } catch (error) {
          this.error(error.message || "Unable to save. Please try again.");
        } finally {
          this.submitting = false;
          submit.disabled = false;
          email.disabled = false;
          if (reminderConsent) reminderConsent.disabled = false;
          back.disabled = false;
          submit.textContent = submitLabel;
        }
      });
      this.fields.append(form);
      const nav = el("nav", "", "quiz-nav");
      nav.append(back);
      this.fields.append(nav);
    }
    renderAnalysis() {
      this.heading.textContent = this.purchaseFlow ? "Checking if you’re a fit" : "Analyzing your profile";
      const list = el("ol", "", "quiz-list");
      list.setAttribute("aria-label", "Checking eligibility");
      const checkSteps = this.purchaseFlow
        ? ["Treatment complete", "Retainer status", "Checking eligibility"]
        : this.adFlow
          ? ["Your treatment", "Your priorities", "Retainer status", "Checking eligibility"]
          : steps;
      this.analysisDone = new Promise((resolve) => { this.resolveAnalysis = resolve; });
      for (const step of checkSteps) {
        const row = el("li");
        row.append(el("span", "", "quiz-marker"), el("span", step));
        list.append(row);
      }
      this.fields.append(list);
      const beforeEmail = this.state.screen === "analysis-email";
      this.status = el("p", beforeEmail ? "Reviewing your answers…" : "Saving your answers…", "quiz-description");
      this.status.role = "status";
      this.fields.append(this.status);
      let index = 0;
      clearInterval(this.analysisTimer);
      this.analysisTimer = setInterval(() => {
        if (index < checkSteps.length)
          list.children[index++].setAttribute("data-done", "");
        if (index === checkSteps.length) {
          clearInterval(this.analysisTimer);
          this.resolveAnalysis?.();
          this.resolveAnalysis = null;
          if (beforeEmail) {
            this.persist({ screen: "email-qualified" });
            this.api.track("quiz_email_gate_viewed", { funnel_variant: this.api.read().funnelVariant || "cleaner-retainer" });
            this.render();
          }
        }
      }, 500);
    }
    renderQualified() {
      this.qualifiedShownAt = performance.now();
      const mark = el("div", "", "quiz-fit-mark");
      mark.setAttribute("aria-hidden", "true");
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("viewBox", "0 0 80 80");
      svg.innerHTML = '<circle cx="40" cy="40" r="35"/><path d="m24 41 11 11 21-25"/>';
      mark.append(svg);
      this.heading.before(mark);
      this.heading.textContent = "You’re a fit.";
      this.fields.append(el("p", "We’ll help with your dental scan after you order.", "quiz-description"));
      this.status = el("p", "Getting checkout ready…", "quiz-fit-status");
      this.status.role = "status";
      this.fields.append(this.status);
    }
    async finishWithAnalysis() {
      this.persist({ screen: "analysis" });
      this.render();
      await this.analysisDone;
      await this.finish();
    }
    async complete() {
      if (this.submitting) return;
      this.submitting = true;
      this.persist({
        screen: this.state.emailGate ? "email-qualified" : "analysis",
        eventId: this.state.eventId || this.api.uuid(),
      });
      if (!this.state.emailGate) this.render();
      try {
        if (!this.api.storageAvailable())
          throw new Error(
            "Please allow session storage in your browser, then try again.",
          );
        const result = await this.api.post(
          "/v1/quiz-submissions",
          { answers: this.state.answers, ...(this.purchaseFlow ? PURCHASE_SUBMISSION : this.adFlow ? AD_SUBMISSION : SUBMISSION), cart_handoff: true,
            ...(this.state.emailGate ? { email: this.state.email } : {}) },
          this.state.eventId,
        );
        if (!result.qualified) {
          this.persist({
            qualified: false,
            attributes: null,
            screen:
              result.held_reason === "In Treatment"
                ? "email-treatment"
                : result.held_reason === "Retainer assessment needed" ? "check-in" : "email-no",
          });
          this.render();
          return;
        }
        if (
          !result.order_id ||
          !result.cart_attributes?.onboarding_token ||
          result.cart_attributes.order_id !== result.order_id
        )
          throw new Error("Unable to prepare your purchase. Please try again.");
        if (this.purchaseFlow) await this.analysisDone;
        this.persist({
          qualified: true,
          attributes: result.cart_attributes,
          screen: "question",
        });
        if (this.state.emailGate) {
          this.api.track("quiz_email_captured", { qualified: true, meta_success: true, event_id: this.state.eventId, funnel_variant: this.api.read().funnelVariant || "cleaner-retainer" });
        } else this.api.track("quiz_completed", { meta_success: true, event_id: this.state.eventId, total_questions: this.activeQuestions.length, quiz_version: this.purchaseFlow ? "purchase-v1" : this.adFlow ? AD_QUIZ_VERSION : "v1" });
      } catch (error) {
        if (this.state.emailGate) {
          this.error(error.message || "Unable to save. Please try again.");
          return;
        }
        this.renderRetry(error.message, () => void this.complete(), () => {
          this.persist({ screen: "question" });
          this.render();
        });
        return;
      } finally {
        clearInterval(this.analysisTimer);
        this.submitting = false;
      }
      await this.finish();
    }
    // Consumes the checkout intent recorded by the cart's Checkout control. A cart failure
    // here retries only this step, so the quiz submission is never posted twice.
    async finish() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        if (!this.state.checkoutIntent) {
          location.assign(this.api.localUrl(this.state.returnUrl));
          return;
        }
        const cart = await window.mosesCart.get();
        if (!cart.items.some(this.api.isRetainer)) {
          this.leave();
          return;
        }
        if (!this.api.qualified())
          throw new Error("Unable to prepare your purchase. Please try again.");
        if (this.purchaseFlow && this.state.screen !== "qualified") {
          this.persist({ screen: "qualified" });
          this.render();
        }
        await this.api.attachCart();
        if (this.purchaseFlow) {
          const remaining = 1200 - (performance.now() - this.qualifiedShownAt);
          if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
        }
        this.persist({ checkoutIntent: false });
        // Replace so Back from checkout returns to the cart, not to a finished quiz.
        location.replace(this.api.config.root + "checkout");
      } catch (error) {
        this.renderRetry(
          error.message,
          () => void this.finish(),
          () => this.leave(),
        );
      } finally {
        this.submitting = false;
      }
    }
    renderRetry(message, retry, back) {
      this.status.textContent = "Your answers are still here.";
      this.error(message || "Unable to save. Please try again.");
      this.fields
        .querySelectorAll(".quiz-submit, .quiz-nav")
        .forEach((node) => node.remove());
      this.fields.append(this.button("Try again", retry, SUBMIT_CLASS));
      const nav = el("nav", "", "quiz-nav");
      nav.append(this.backButton(back));
      this.fields.append(nav);
    }
  }
  if (!customElements.get("moses-quiz"))
    customElements.define("moses-quiz", MosesQuiz);
})();
